import { useState } from "react";
import { isProxyableCopilotImage } from "@yazamco/validation";

/**
 * A picture inside an answer.
 *
 * Copilot embeds images as ordinary Markdown, and until this component existed
 * every one of them rendered as alt text and nothing else. The address it
 * writes points at Microsoft's own attachment storage, which is neither
 * public — an unauthenticated GET gets a 401 — nor allowed by the page's
 * `img-src 'self' data:` policy. So the image is requested from OUR API
 * instead (`GET /copilot/images?url=…`), which is same-origin and can present
 * the reader's own Microsoft token upstream.
 *
 * Anything the API will not fetch — an image on some other host, or one it
 * fetched and was refused — becomes a link rather than a broken picture. A
 * reader who can see that there is an image, and open it, is in a better
 * position than one looking at a grey box, and the page never quietly pretends
 * the answer had no illustration.
 */
export function AnswerImage({
  src,
  alt,
  title,
}: {
  src?: string;
  alt?: string;
  title?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (!src) return null;
  src = normalizeCopilotAnswerUrl(src);
  // Inline images need no help: the page's own policy already allows `data:`.
  if (src.startsWith("data:image/")) {
    return <img className="cp-answer-image" src={src} alt={alt ?? ""} title={title} />;
  }

  const proxied = isProxyableCopilotImage(src);
  if (proxied && !failed) {
    return (
      <img
        className="cp-answer-image"
        src={`/api/v1/copilot/images?url=${encodeURIComponent(src)}`}
        alt={alt ?? ""}
        title={title}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return <ImageFallback src={src} alt={alt} refused={proxied} />;
}

/**
 * Work IQ occasionally serializes a URL as a complete Markdown link inside
 * the destination of another link/image. React Markdown then quite correctly
 * hands us `[https://…](https://…)` as the URL, which is not fetchable. Peel
 * off exactly that redundant layer, but only when its destination is an
 * absolute HTTP(S) URL. The proxy allowlist remains the security boundary for
 * images.
 */
export function normalizeCopilotAnswerUrl(raw: string): string {
  const value = raw.trim();
  if (!value.startsWith("[") || !value.endsWith(")")) return raw;

  const separator = value.indexOf("](");
  if (separator < 2) return raw;

  const label = value.slice(1, separator).trim();
  const destination = value.slice(separator + 2, -1).trim();
  if (!/^https?:\/\//i.test(label) || !/^https?:\/\//i.test(destination)) {
    return raw;
  }

  try {
    const url = new URL(destination);
    return url.protocol === "https:" || url.protocol === "http:" ? destination : raw;
  } catch {
    return raw;
  }
}

/**
 * What to show instead of the picture.
 *
 * The href is checked rather than trusted: this text came back from a model, so
 * a `javascript:` or `data:` URL is a possibility however unlikely, and one
 * that would run in the reader's session if it were put in an anchor. Anything
 * that is not plain http(s) is stated in words with no link at all.
 */
function ImageFallback({
  src,
  alt,
  refused,
}: {
  src: string;
  alt?: string;
  refused: boolean;
}) {
  const label = alt?.trim() || "Image";
  const note = refused
    ? "could not be loaded"
    : "is hosted outside Microsoft 365 and is not loaded here";

  let safe = false;
  try {
    const url = new URL(src, window.location.origin);
    safe = url.protocol === "https:" || url.protocol === "http:";
  } catch {
    safe = false;
  }

  return (
    <span className="cp-answer-image-fallback">
      {safe ? (
        <a href={src} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ) : (
        label
      )}{" "}
      <span>({note})</span>
    </span>
  );
}
